import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import { enqueueSnackbar } from 'notistack'
import moment from 'moment-timezone'
import { Link } from 'react-router-dom'

import { AppContext } from '/providers/app-context'
import {
  downloadClientCsr,
  getCertificateManagementRows,
  getClientCertificates,
  requestPrivateCertificate,
  setClientCertificateType,
  uploadOwnAppleCertificate,
  uploadOwnGoogleCredentials
} from '/providers/studio-api'
import StyledPaper from '../paper.jsx'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'

const emptyState = {
  apple: {
    selectedType: "shared",
    certificateActive: false,
    usingSharedCertificate: true,
    canChangeType: true,
    csrAvailable: false,
    configured: false,
    passTypeId: "",
    teamId: "",
    expiryDate: "",
    appleEmail: "",
    defaultAppleEmail: "",
    optionDetails: [],
    requiresOwnCertificate: false
  },
  google: {
    selectedType: "shared",
    certificateActive: false,
    usingSharedCertificate: true,
    canChangeType: true,
    configured: false,
    issuerId: "",
    optionDetails: [],
    requiresOwnCertificate: false
  }
}

const formatDate = ymd => ymd ? moment(ymd, "YYYYMMDD").format("DD MMM YYYY") : "Not set"

const getOptionLabel = (option, provider) => {
  const labels = {
    shared: provider === "google" ? "Shared Credentials" : "Shared Certificate",
    private: provider === "google" ? "Private Credentials" : "Private Certificate",
    own: provider === "google" ? "Supply Own Credentials" : "Supply Own Certificate"
  }

  if (option.type === "private" && option.credits > 0) {
    return `${labels[option.type]} (${option.credits.toLocaleString()} credits per month)`
  }

  return labels[option.type]
}

const CertificateActions = ({
  provider,
  certificate,
  selectedType,
  setSelectedType,
  appleEmail,
  setAppleEmail,
  introAccepted,
  setIntroAccepted,
  pricingAccepted,
  setPricingAccepted,
  applePriceLabel,
  requiresPricingAgreement,
  onboardingAnniversaryDay,
  processing,
  loading,
  saveSelection
}) => {
  const changed = provider === "apple"
    ? selectedType !== certificate.selectedType || (selectedType === "own" && appleEmail !== (certificate.appleEmail || certificate.defaultAppleEmail || ""))
    : selectedType !== certificate.selectedType

  const privateRequested = selectedType === "private" && selectedType !== certificate.selectedType

  return (
    <div className='flex flex-col gap-4'>
      {!certificate.canChangeType ? <Alert severity='info'>{provider === "apple" ? "Apple certificate" : "Google credentials"} are locked because they are active.</Alert> : null}
      {certificate.requiresOwnCertificate ? <Alert severity='warning'>Clients on postpay tariffs must use their own {provider === "apple" ? "Apple certificate" : "Google credentials"}.</Alert> : null}
      {privateRequested ? (
        <Alert severity='info'>
          Saving this change will create a support request for Smartix to provision a private {provider === "apple" ? "Apple certificate" : "Google credential set"} for this client.
        </Alert>
      ) : null}

      <RadioGroup value={selectedType} onChange={event => setSelectedType(event.target.value)}>
        {certificate.optionDetails.map(option => (
          <FormControlLabel
            key={option.type}
            value={option.type}
            control={<Radio />}
            label={getOptionLabel(option, provider)}
            disabled={!certificate.canChangeType}
          />
        ))}
      </RadioGroup>

      {provider === "apple" && selectedType === "own" && certificate.canChangeType ? (
        <TextField
          label='Apple developer account email'
          value={appleEmail}
          onChange={event => setAppleEmail(event.target.value)}
          size='small'
          helperText='Used in the CSR request and stored for future certificate renewals.'
        />
      ) : null}

      {provider === "apple" && requiresPricingAgreement ? (
        <FormControlLabel
          control={<Checkbox checked={pricingAccepted} onChange={event => setPricingAccepted(event.target.checked)} />}
          label={`I agree that ${applePriceLabel} will be billed on the monthly anniversary of the onboarding date for this Apple certificate option${onboardingAnniversaryDay ? ` (${onboardingAnniversaryDay})` : ""}.`}
        />
      ) : null}

      <FormControlLabel
        control={<Checkbox checked={introAccepted} onChange={event => setIntroAccepted(event.target.checked)} />}
        label='I have read and understood the certificate guidance.'
      />

      <div className='flex flex-row justify-end'>
        <Button
          variant='contained'
          onClick={() => saveSelection(provider)}
          disabled={processing || loading || !certificate.canChangeType || !changed || !introAccepted || (requiresPricingAgreement && !pricingAccepted)}
        >
          {privateRequested ? `Request private ${provider === "apple" ? "certificate" : "credentials"}` : `Save ${provider === "apple" ? "Apple" : "Google"} selection`}
        </Button>
      </div>
    </div>
  )
}

const AccountCertificates = () => {
  const { apiSource, apiKey, processing, setProcessing, selectedClientId } = useContext(AppContext)

  const [rows, setRows] = useState([])
  const [loadingRows, setLoadingRows] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [activeClientId, setActiveClientId] = useState(null)
  const [certificate, setCertificate] = useState(emptyState)
  const [loadingDetail, setLoadingDetail] = useState(false)

  const [appleType, setAppleType] = useState("shared")
  const [googleType, setGoogleType] = useState("shared")
  const [appleEmail, setAppleEmail] = useState("")
  const [applePemName, setApplePemName] = useState("")
  const [googleJsonName, setGoogleJsonName] = useState("")
  const [applePem, setApplePem] = useState("")
  const [googleCredentials, setGoogleCredentials] = useState(null)
  const [applePricingAccepted, setApplePricingAccepted] = useState(false)
  const [appleIntroAccepted, setAppleIntroAccepted] = useState(false)
  const [googleIntroAccepted, setGoogleIntroAccepted] = useState(false)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const appleInputRef = useRef(null)
  const googleInputRef = useRef(null)

  const activeRow = useMemo(
    () => rows.find(row => row.client_id?.toString() === activeClientId?.toString()) ?? null,
    [rows, activeClientId]
  )

  const refreshRows = async () => {
    setLoadingRows(true)
    const result = await getCertificateManagementRows({ apiSource, apiKey })

    if (result.ok) {
      setRows(result.rows ?? [])
    } else {
      showActionError(result.error, 'Error getting certificate management rows')
    }

    setLoadingRows(false)
  }

  const applyCertificateState = nextCertificate => {
    setCertificate(nextCertificate)
    setAppleType(nextCertificate.apple.selectedType)
    setGoogleType(nextCertificate.google.selectedType)
    setAppleEmail(nextCertificate.apple.appleEmail || nextCertificate.apple.defaultAppleEmail || "")
    setApplePricingAccepted(false)
    setAppleIntroAccepted(false)
    setGoogleIntroAccepted(false)
    setApplePem("")
    setApplePemName("")
    setGoogleCredentials(null)
    setGoogleJsonName("")
    if (appleInputRef.current) appleInputRef.current.value = ""
    if (googleInputRef.current) googleInputRef.current.value = ""
  }

  const loadCertificateDetail = async clientId => {
    if (clientId == null) return

    setLoadingDetail(true)
    const result = await getClientCertificates({ apiSource, apiKey, clientId })

    if (result.ok) {
      applyCertificateState(result.certificate)
    } else {
      applyCertificateState(emptyState)
      showActionError(result.error, 'Error getting certificate details')
    }

    setLoadingDetail(false)
  }

  useEffect(() => {
    refreshRows()
  }, [apiKey, apiSource, selectedClientId])

  const openClientDrawer = async row => {
    setDrawerOpen(true)
    setActiveClientId(row.client_id)
    applyCertificateState(emptyState)
    await loadCertificateDetail(row.client_id)
  }

  const readTextFile = file => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => resolve(event.target.result)
    reader.onerror = reject
    reader.readAsText(file)
  })

  const refreshAfterChange = async clientId => {
    await Promise.all([refreshRows(), loadCertificateDetail(clientId)])
  }

  const saveSelection = async provider => {
    if (activeClientId == null) return

    const certificateType = provider === "apple" ? appleType : googleType
    const isPrivateRequest = certificateType === "private" && certificateType !== certificate[provider].selectedType

    setProcessing(true)

    if (isPrivateRequest) {
      enqueueSnackbar({ message: `Requesting private ${provider === "apple" ? "Apple certificate" : "Google credentials"}`, variant: 'light' })
      const result = await requestPrivateCertificate({ apiSource, apiKey, clientId: activeClientId, provider })

      if (result.ok) {
        enqueueSnackbar({ message: 'Support request created', variant: 'success' })
        await refreshAfterChange(activeClientId)
      } else {
        showActionError(result.error, `Error requesting private ${provider} certificate`)
      }

      setProcessing(false)
      return
    }

    enqueueSnackbar({ message: `Saving ${provider} certificate preference`, variant: 'light' })
    const result = await setClientCertificateType({
      apiSource,
      apiKey,
      clientId: activeClientId,
      provider,
      certificateType,
      appleEmail
    })

    if (result.ok) {
      enqueueSnackbar({ message: `${provider === "apple" ? "Apple" : "Google"} certificate preference saved`, variant: 'success' })
      applyCertificateState(result.certificate)
      await refreshRows()
    } else {
      showActionError(result.error, `Error saving ${provider} certificate preference`)
    }

    setProcessing(false)
  }

  const handleDownloadCsr = async () => {
    if (activeClientId == null) return

    setProcessing(true)
    const result = await downloadClientCsr({ apiSource, apiKey, clientId: activeClientId })

    if (result.ok) {
      const blob = new Blob([result.csr_pem], { type: 'application/x-pem-file' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = result.filename
      anchor.click()
      window.URL.revokeObjectURL(url)
      enqueueSnackbar({ message: 'CSR downloaded', variant: 'success' })
    } else {
      showActionError(result.error, 'Error downloading CSR')
    }

    setProcessing(false)
  }

  const handleAppleFile = async event => {
    const file = event.target.files?.[0]
    if (!file) return

    const pem = await readTextFile(file)
    setApplePemName(file.name)
    setApplePem(pem)
  }

  const handleGoogleFile = async event => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const raw = await readTextFile(file)
      setGoogleJsonName(file.name)
      setGoogleCredentials(JSON.parse(raw))
    }
    catch {
      enqueueSnackbar({ message: 'Google credentials must be valid JSON', variant: 'error' })
    }
  }

  const saveAppleCertificate = async () => {
    if (activeClientId == null) return

    setProcessing(true)
    enqueueSnackbar({ message: 'Uploading Apple certificate', variant: 'light' })
    const result = await uploadOwnAppleCertificate({ apiSource, apiKey, clientId: activeClientId, pem: applePem })

    if (result.ok) {
      enqueueSnackbar({ message: 'Apple certificate uploaded', variant: 'success' })
      applyCertificateState(result.certificate)
      await refreshRows()
    } else {
      showActionError(result.error, 'Error uploading Apple certificate')
    }

    setProcessing(false)
  }

  const saveGoogleCredentials = async () => {
    if (activeClientId == null) return

    setProcessing(true)
    enqueueSnackbar({ message: 'Uploading Google credentials', variant: 'light' })
    const result = await uploadOwnGoogleCredentials({ apiSource, apiKey, clientId: activeClientId, credentials: googleCredentials })

    if (result.ok) {
      enqueueSnackbar({ message: 'Google credentials uploaded', variant: 'success' })
      applyCertificateState(result.certificate)
      await refreshRows()
    } else {
      showActionError(result.error, 'Error uploading Google credentials')
    }

    setProcessing(false)
  }

  const selectedAppleOption = certificate.apple.optionDetails.find(option => option.type === appleType)
  const requiresApplePricingAgreement = appleType !== certificate.apple.selectedType && (selectedAppleOption?.credits ?? 0) > 0
  const applePriceLabel = selectedAppleOption ? `${selectedAppleOption.credits.toLocaleString()} credits per month` : ""

  useEffect(() => {
    setApplePricingAccepted(false)
  }, [appleType, activeClientId])

  return (
    <>
      <div className='flex h-full min-h-0 flex-col gap-5 overflow-hidden p-5'>
        <Alert severity='warning'>
          Moving away from shared certificates after live passes already exist is a significant operational change. If you are planning that change, discuss it first in <Link to='/support/history' className='link'>Support History</Link>.
        </Alert>

        <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className='flex min-h-0 flex-1 flex-col gap-4 p-5'>
            <Typography variant='h5'>Client certificate summary</Typography>

            <TableContainer sx={{ flex: 1, minHeight: 0 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Client ID</TableCell>
                    <TableCell>Client name</TableCell>
                    <TableCell>Apple type</TableCell>
                    <TableCell>Google type</TableCell>
                    <TableCell>Apple expiry</TableCell>
                    <TableCell>Next replacement</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow
                      key={row.client_id}
                      hover
                      selected={activeClientId?.toString() === row.client_id?.toString()}
                      onClick={() => openClientDrawer(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{row.client_id}</TableCell>
                      <TableCell>{row.client_name}</TableCell>
                      <TableCell className='capitalize'>{row.apple_certificate_type}</TableCell>
                      <TableCell className='capitalize'>{row.google_certificate_type}</TableCell>
                      <TableCell>{formatDate(row.apple_expiry_date)}</TableCell>
                      <TableCell sx={row.apple_replacement_overdue ? { color: 'error.main', fontWeight: 700 } : undefined}>
                        {formatDate(row.apple_replacement_date)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loadingRows && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>No client certificate rows found.</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </StyledPaper>
      </div>

      <Drawer
        open={drawerOpen}
        anchor="right"
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: 560,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: 560, boxSizing: 'border-box', padding: 5, paddingTop: 10 },
        }}
      >
        <div className='flex flex-col gap-5 overflow-y-auto'>
          <div className='flex flex-row justify-between items-start gap-4'>
            <div className='flex flex-col gap-1'>
              <Typography color="secondary" variant="h6">
                {activeRow ? `Certificates: ${activeRow.client_name}` : 'Certificates'}
              </Typography>
              {activeRow ? <Typography variant='body2'>Client ID: {activeRow.client_id}</Typography> : null}
            </div>
            <Button endIcon={<EastRoundedIcon />} onClick={() => setDrawerOpen(false)}>cancel</Button>
          </div>

          {loadingDetail ? <Alert severity='info'>Loading certificate details…</Alert> : null}

          {!loadingDetail && activeRow ? (
            <>
              <Alert severity='info'>
                Apple private certificates are provisioned by Smartix. Apple and Google own credentials remain client-managed. Private certificates are only available to prepay customers.
              </Alert>

              <div className='flex flex-col gap-3'>
                <Typography variant='h5'>Apple</Typography>
                <Typography variant='body2'>Current type: <span className='capitalize'>{certificate.apple.selectedType}</span></Typography>
                <Typography variant='body2'>Installed: {certificate.apple.configured ? "Yes" : "No"}</Typography>
                <Typography variant='body2'>Expiry: {formatDate(certificate.apple.expiryDate)}</Typography>
                {certificate.apple.passTypeId ? <Typography variant='body2'>Pass type ID: {certificate.apple.passTypeId}</Typography> : null}
                {certificate.apple.teamId ? <Typography variant='body2'>Team ID: {certificate.apple.teamId}</Typography> : null}

                <CertificateActions
                  provider="apple"
                  certificate={certificate.apple}
                  selectedType={appleType}
                  setSelectedType={setAppleType}
                  appleEmail={appleEmail}
                  setAppleEmail={setAppleEmail}
                  introAccepted={appleIntroAccepted}
                  setIntroAccepted={setAppleIntroAccepted}
                  pricingAccepted={applePricingAccepted}
                  setPricingAccepted={setApplePricingAccepted}
                  applePriceLabel={applePriceLabel}
                  requiresPricingAgreement={requiresApplePricingAgreement}
                  onboardingAnniversaryDay=""
                  processing={processing}
                  loading={loadingDetail}
                  saveSelection={saveSelection}
                />

                {certificate.apple.selectedType === "own" ? (
                  <div className='flex flex-col gap-4'>
                    <Divider />
                    <Typography variant='h6'>Apple CSR and PEM upload</Typography>
                    <Button variant='outlined' onClick={handleDownloadCsr} disabled={processing || !certificate.apple.csrAvailable}>
                      Download CSR
                    </Button>
                    <input ref={appleInputRef} type='file' accept='.pem' onChange={handleAppleFile} />
                    {applePemName ? <Typography variant='body2'>Selected file: {applePemName}</Typography> : null}
                    <div className='flex flex-row justify-end'>
                      <Button variant='contained' onClick={saveAppleCertificate} disabled={processing || !applePem}>
                        Upload Apple certificate
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>

              <Divider />

              <div className='flex flex-col gap-3'>
                <Typography variant='h5'>Google</Typography>
                <Typography variant='body2'>Current type: <span className='capitalize'>{certificate.google.selectedType}</span></Typography>
                <Typography variant='body2'>Installed: {certificate.google.configured ? "Yes" : "No"}</Typography>
                {certificate.google.issuerId ? <Typography variant='body2'>Issuer ID: {certificate.google.issuerId}</Typography> : null}

                <CertificateActions
                  provider="google"
                  certificate={certificate.google}
                  selectedType={googleType}
                  setSelectedType={setGoogleType}
                  appleEmail=""
                  setAppleEmail={() => {}}
                  introAccepted={googleIntroAccepted}
                  setIntroAccepted={setGoogleIntroAccepted}
                  pricingAccepted={false}
                  setPricingAccepted={() => {}}
                  applePriceLabel=""
                  requiresPricingAgreement={false}
                  onboardingAnniversaryDay=""
                  processing={processing}
                  loading={loadingDetail}
                  saveSelection={saveSelection}
                />

                {certificate.google.selectedType === "own" ? (
                  <div className='flex flex-col gap-4'>
                    <Divider />
                    <Typography variant='h6'>Google JSON upload</Typography>
                    <input ref={googleInputRef} type='file' accept='.json,application/json' onChange={handleGoogleFile} />
                    {googleJsonName ? <Typography variant='body2'>Selected file: {googleJsonName}</Typography> : null}
                    <div className='flex flex-row justify-end'>
                      <Button variant='contained' onClick={saveGoogleCredentials} disabled={processing || !googleCredentials}>
                        Upload Google credentials
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </Drawer>

      <ActionErrorDialog title="Certificate action failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default AccountCertificates
